import React, { useState, useEffect, useRef } from 'react';
import './App.css';

// Layout components
import Header from './components/Layout/Header';
import BottomNavigation from './components/Layout/BottomNavigation';

// Routes
import SelectorScreen from './routes/SelectorScreen';
import FavoritesScreen from './routes/FavoritesScreen';
import FaqScreen from './routes/FaqScreen';
import BlogScreen from './routes/BlogScreen';
import ContactsScreen from './routes/ContactsScreen';
import CartScreen from './routes/CartScreen';

// Models
import { getWithExpiry, setWithExpiry } from './models/storageModel';
import { findRackById } from './models/cartModel';
import { toggleFavorite, isFavorite } from './models/favoritesModel';

import { initTelegramApp } from './services/telegramService';

// Data
import carsData from './data/cars.json';
import racksData from './data/racks.json';
import adaptersData from './data/adapters.json';



function App() {
  // State management
  const [activeTab, setActiveTab] = useState('selector');
  const [darkMode, setDarkMode] = useState(
    getWithExpiry('darkMode') !== null
      ? getWithExpiry('darkMode')
      : window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const [selectedBrand, setSelectedBrand] = useState(getWithExpiry('selectedBrand') || '');
  const [selectedModel, setSelectedModel] = useState(getWithExpiry('selectedModel') || '');
  const [selectedModification, setSelectedModification] = useState(getWithExpiry('selectedModification') || '');
  const [selectedRoofType, setSelectedRoofType] = useState(getWithExpiry('selectedRoofType') || '');
  const [racks, setRacks] = useState([]);
  const [filteredRacks, setFilteredRacks] = useState([]);
  const [availableRacks, setAvailableRacks] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [cart, setCart] = useState([]);
  const [checkoutStep, setCheckoutStep] = useState(0);
  const [formData, setFormData] = useState({
    fullName: '',
    //email: '',
    phone: '+7',
    telegramUsername: '',
    deliveryMethod: 'pickup',
    address: '',
    comments: ''
  });
  const [currentSlide, setCurrentSlide] = useState({});
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(getWithExpiry('filters') || {
    brands: [],
    colors: [],
    barTypes: [],
    countries: [],
    capacities: [],
    lock: null,
    priceRange: null
  });

  // Dropdown state management
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Инициализация данных после загрузки компонента
  useEffect(() => {
    initTelegramApp();
    // Инициализация избранного
    const savedFavoriteIds = getWithExpiry('favorites') || [];
    if (savedFavoriteIds.length > 0) {
      const loadedFavorites = savedFavoriteIds.map(id => findRackById(id)).filter(Boolean);
      setFavorites(loadedFavorites);
    }

    // Инициализация корзины
    const savedCartItems = getWithExpiry('cart') || [];
    if (savedCartItems.length > 0) {
      const loadedCart = savedCartItems.map(item => ({
        rack: findRackById(
          item.rackId,
          item.adapterInfo,
          item.needsAdapter,
          item.adapterPrice,
          item.carId
        ),
        car: item.car,
        quantity: item.quantity
      })).filter(item => item.rack);

      setCart(loadedCart);
    }

    // Инициализация слайдера
    const initialSlides = {};
    racksData.forEach(rack => {
      initialSlides[rack.id] = 0;
    });
    setCurrentSlide(initialSlides);

    // Если все параметры автомобиля выбраны, показываем результаты
    if (selectedBrand && selectedModel && selectedModification && selectedRoofType) {
      // Небольшая задержка для уверенности, что все компоненты загрузились
      setTimeout(() => {
        handleSearch();
      }, 100);
    }

    // Отмечаем, что начальная загрузка завершена
    setInitialLoadComplete(true);
  }, []);

  // Сохранение данных в localStorage при их изменении
  useEffect(() => {
    const favoriteIds = favorites.map(rack => rack.id);
    setWithExpiry('favorites', favoriteIds);
  }, [favorites]);

  useEffect(() => {
    const simplifiedCart = cart.map(item => ({
      rackId: item.rack.id,
      car: item.car,
      quantity: item.quantity,
      needsAdapter: item.rack.needsAdapter || false,
      adapterInfo: item.rack.adapterInfo || null,
      adapterPrice: item.rack.adapterPrice || 0,
      carId: item.rack.carId || null
    }));
    setWithExpiry('cart', simplifiedCart);
  }, [cart]);

  useEffect(() => {
    setWithExpiry('selectedBrand', selectedBrand);
  }, [selectedBrand]);

  useEffect(() => {
    setWithExpiry('selectedModel', selectedModel);
  }, [selectedModel]);

  useEffect(() => {
    setWithExpiry('selectedModification', selectedModification);
  }, [selectedModification]);

  useEffect(() => {
    setWithExpiry('selectedRoofType', selectedRoofType);
  }, [selectedRoofType]);

  useEffect(() => {
    setWithExpiry('darkMode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    setWithExpiry('filters', filters);
  }, [filters]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Reset dependent fields when parent selection changes, но только после начальной загрузки
  useEffect(() => {
    if (initialLoadComplete && !getWithExpiry('selectedModel')) {
      setSelectedModel('');
      setSelectedModification('');
      setSelectedRoofType('');
      setShowResults(false);
    }
  }, [selectedBrand, initialLoadComplete]);

  useEffect(() => {
    if (initialLoadComplete && !getWithExpiry('selectedModification')) {
      setSelectedModification('');
      setSelectedRoofType('');
      setShowResults(false);
    }
  }, [selectedModel, initialLoadComplete]);

  useEffect(() => {
    if (initialLoadComplete && !getWithExpiry('selectedRoofType')) {
      setSelectedRoofType('');
      setShowResults(false);
    }
  }, [selectedModification, initialLoadComplete]);

  // Применение фильтров при их изменении
  useEffect(() => {
    if (!showResults || availableRacks.length === 0) return;

    let filtered = [...availableRacks];

    // Фильтрация по бренду
    if (filters.brands.length > 0) {
      filtered = filtered.filter(rack => filters.brands.includes(rack.brand));
    }

    // Фильтрация по цвету
    if (filters.colors.length > 0) {
      filtered = filtered.filter(rack => filters.colors.includes(rack.color));
    }

    // Фильтрация по типу дуги
    if (filters.barTypes.length > 0) {
      filtered = filtered.filter(rack => filters.barTypes.includes(rack.bar_type));
    }

    // Фильтрация по стране
    if (filters.countries.length > 0) {
      filtered = filtered.filter(rack => filters.countries.includes(rack.country));
    }

    // Фильтрация по грузоподъемности
    if (filters.capacities.length > 0) {
      filtered = filtered.filter(rack => filters.capacities.includes(rack.capacity));
    }

    // Фильтрация по наличию замка
    if (filters.lock) {
      filtered = filtered.filter(rack => rack.lock === filters.lock);
    }

    // Фильтрация по ценовому диапазону
    if (filters.priceRange) {
      filtered = filtered.filter(rack => {
        const totalPrice = rack.price + (rack.needsAdapter ? rack.adapterPrice : 0);
        const range = filters.priceRange;

        if (range.min && range.max) {
          return totalPrice >= range.min && totalPrice <= range.max;
        } else if (range.min) {
          return totalPrice >= range.min;
        } else if (range.max) {
          return totalPrice <= range.max;
        }
        return true;
      });
    }

    setFilteredRacks(filtered);
  }, [filters, availableRacks, showResults]);

  const handleSearch = () => {
    // Сбрасываем фильтры при новом поиске
    const emptyFilters = {
      brands: [],
      colors: [],
      barTypes: [],
      countries: [],
      capacities: [],
      lock: null,
      priceRange: null
    };

    setFilters(emptyFilters);
    setWithExpiry('filters', emptyFilters);

    // Находим все автомобили, соответствующие выбранным параметрам
    const selectedCars = carsData.filter(car =>
      car.brand === selectedBrand &&
      car.model === selectedModel &&
      car.modification === selectedModification &&
      car.roof_type === selectedRoofType
    );

    if (selectedCars.length === 0) {
      setRacks([]);
      setFilteredRacks([]);
      setAvailableRacks([]);
      setShowResults(false);
      return;
    }

    // Создаем массив для хранения всех подходящих багажников
    let availableRacksData = [];

    // Для каждого найденного автомобиля находим соответствующий багажник
    selectedCars.forEach(selectedCar => {
      // Находим багажник по ID
      const rack = racksData.find(rack => rack.id === selectedCar.rack_id);

      if (rack) {
        // Если нужен адаптер, добавляем информацию о нем
        if (selectedCar.adapter) {
          // Преобразуем ID адаптера в строку для корректного сравнения
          const adapterId = String(selectedCar.adapter);
          const adapter = adaptersData.find(a => String(a.id) === adapterId);

          availableRacksData.push({
            ...rack,
            needsAdapter: true,
            adapterInfo: adapter,
            adapterPrice: adapter ? adapter.price : 0,
            carId: selectedCar.id // Добавляем ID автомобиля для уникальности
          });
        } else {
          availableRacksData.push({
            ...rack,
            needsAdapter: false,
            adapterInfo: null,
            adapterPrice: 0,
            carId: selectedCar.id // Добавляем ID автомобиля для уникальности
          });
        }
      }
    });

    console.log('Search results:', availableRacksData); // Отладочный вывод

    setRacks(availableRacksData);
    setFilteredRacks(availableRacksData);
    setAvailableRacks(availableRacksData);
    setShowResults(true);
  };

  // Обновленная функция addToCart в App.js
  const addToCart = (rack) => {
    const carInfo = {
      brand: selectedBrand,
      model: selectedModel,
      modification: selectedModification,
      roofType: selectedRoofType
    };

    setCart(prev => {
      // Check if this rack with the same car is already in cart
      const existingItemIndex = prev.findIndex(item =>
        item.rack.id === rack.id &&
        item.car.brand === carInfo.brand &&
        item.car.model === carInfo.model &&
        item.car.modification === carInfo.modification &&
        item.car.roofType === carInfo.roofType
      );

      if (existingItemIndex >= 0) {
        // If it exists, remove it (toggle behavior)
        return prev.filter((_, index) => index !== existingItemIndex);
      } else {
        // Otherwise add it
        return [...prev, { rack, car: carInfo, quantity: 1 }];
      }
    });
  };

  // Обновленная функция isInCart в App.js
  const isInCart = (rackId) => {
    // Проверяем, есть ли товар в корзине с любыми параметрами автомобиля
    const inCartWithAnyParams = cart.some(item => item.rack.id === rackId);

    // Если выбраны все параметры автомобиля, проверяем также соответствие этим параметрам
    if (selectedBrand && selectedModel && selectedModification && selectedRoofType) {
      return cart.some(item =>
        item.rack.id === rackId &&
        item.car.brand === selectedBrand &&
        item.car.model === selectedModel &&
        item.car.modification === selectedModification &&
        item.car.roofType === selectedRoofType
      );
    }

    // Если не все параметры выбраны, просто проверяем наличие в корзине
    return inCartWithAnyParams;
  };

  const removeFromCart = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };

  const updateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;

    setCart(prev => {
      const newCart = [...prev];
      newCart[index].quantity = newQuantity;
      return newCart;
    });
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      const rackPrice = item.rack.price;
      const adapterPrice = item.rack.needsAdapter ? item.rack.adapterPrice : 0;
      return total + ((rackPrice + adapterPrice) * item.quantity);
    }, 0);
  };

  const calculateDeliveryPrice = () => {
    const total = calculateTotal();

    if (formData.deliveryMethod === 'pickup') {
      return 0;
    } else if (formData.deliveryMethod === 'courier') {
      return total >= 10000 ? 0 : 500;
    } else if (formData.deliveryMethod === 'transport') {
      return 0; // Free to transport company terminal
    }

    return 0;
  };

  const getPaymentMethod = () => {
    switch (formData.deliveryMethod) {
      case 'pickup':
        return 'Банковской картой или наличными';
      case 'courier':
        return 'Наличные';
      case 'transport':
        return 'Банковской картой';
      default:
        return '';
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDeliveryMethodChange = (method) => {
    setFormData(prev => ({
      ...prev,
      deliveryMethod: method
    }));
  };

  const handleCheckout = () => {
    setCheckoutStep(1);
    setActiveTab('cart');
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();

    // Импортируем функции для работы с Telegram
    const { sendTelegramMessage, formatOrderMessage } = await import('./models/telegramModel');

    // Токен бота
    const botToken = '5882785215:AAHaqh01RiEAbFLnqd17b4tIUvSKGNHypT8';
    // ID пользователя для отправки сообщений
    const chatId = '183708850';

    // Форматируем сообщение о заказе
    const message = formatOrderMessage(
      formData,
      cart,
      {
        brand: selectedBrand,
        model: selectedModel,
        modification: selectedModification,
        roofType: selectedRoofType
      },
      calculateTotal(),
      calculateDeliveryPrice()
    );

    // Отправляем сообщение в Telegram
    const success = await sendTelegramMessage(chatId, message, botToken);

    if (success) {
      alert('Заказ успешно оформлен!');
    } else {
      alert('Заказ оформлен, но возникла ошибка при отправке уведомления.');
    }

    // Очищаем корзину и возвращаемся на главный экран
    setCart([]);
    setCheckoutStep(0);
    setActiveTab('selector');
    setFormData({
      fullName: '',
      //email: '',
      phone: '+7',
      telegramUsername: '',
      deliveryMethod: 'pickup',
      address: '',
      comments: ''
    });
  };

  const handleNextSlide = (rackId) => {
    setCurrentSlide(prev => {
      const rack = racksData.find(r => r.id === rackId);
      const photoCount = rack?.photo?.length || 1;
      return {
        ...prev,
        [rackId]: (prev[rackId] + 1) % photoCount
      };
    });
  };

  const handlePrevSlide = (rackId) => {
    setCurrentSlide(prev => {
      const rack = racksData.find(r => r.id === rackId);
      const photoCount = rack?.photo?.length || 1;
      return {
        ...prev,
        [rackId]: (prev[rackId] - 1 + photoCount) % photoCount
      };
    });
  };

  const resetFilters = () => {
    setFilters({
      brands: [],
      colors: [],
      barTypes: [],
      countries: [],
      capacities: [],
      lock: null,
      priceRange: null
    });

    // При сбросе фильтров восстанавливаем все доступные багажники
    setFilteredRacks(availableRacks);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'selector':
        return (
          <SelectorScreen
            darkMode={darkMode}
            selectedBrand={selectedBrand}
            setSelectedBrand={setSelectedBrand}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            selectedModification={selectedModification}
            setSelectedModification={setSelectedModification}
            selectedRoofType={selectedRoofType}
            setSelectedRoofType={setSelectedRoofType}
            showResults={showResults}
            setShowResults={setShowResults}
            handleSearch={handleSearch}
            racks={racks}
            filteredRacks={filteredRacks}
            setFilteredRacks={setFilteredRacks}
            availableRacks={availableRacks}
            setAvailableRacks={setAvailableRacks}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            filters={filters}
            setFilters={setFilters}
            resetFilters={resetFilters}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            dropdownRef={dropdownRef}
            currentSlide={currentSlide}
            handleNextSlide={handleNextSlide}
            handlePrevSlide={handlePrevSlide}
            addToCart={addToCart}
            isInCart={isInCart}
            toggleFavorite={(rack) => toggleFavorite(rack, favorites, setFavorites)}
            isFavorite={(rackId) => isFavorite(rackId, favorites)}
          />
        );
      case 'favorites':
        return (
          <FavoritesScreen
            darkMode={darkMode}
            favorites={favorites}
            currentSlide={currentSlide}
            handleNextSlide={handleNextSlide}
            handlePrevSlide={handlePrevSlide}
            addToCart={addToCart}
            isInCart={isInCart}
            toggleFavorite={(rack) => toggleFavorite(rack, favorites, setFavorites)}
            isFavorite={(rackId) => isFavorite(rackId, favorites)}
          />
        );
      case 'faq':
        return <FaqScreen darkMode={darkMode} />;
      case 'blog':
        return <BlogScreen darkMode={darkMode} />;
      case 'contacts':
        return <ContactsScreen darkMode={darkMode} />;
      case 'cart':
        return (
          <CartScreen
            darkMode={darkMode}
            cart={cart}
            checkoutStep={checkoutStep}
            setCheckoutStep={setCheckoutStep}
            formData={formData}
            handleFormChange={handleFormChange}
            handleDeliveryMethodChange={handleDeliveryMethodChange}
            handleSubmitOrder={handleSubmitOrder}
            removeFromCart={removeFromCart}
            updateQuantity={updateQuantity}
            calculateTotal={calculateTotal}
            calculateDeliveryPrice={calculateDeliveryPrice}
            getPaymentMethod={getPaymentMethod}
            handleCheckout={handleCheckout}
            setActiveTab={setActiveTab}
          />
        );
      default:
        return (
          <SelectorScreen
            darkMode={darkMode}
            selectedBrand={selectedBrand}
            setSelectedBrand={setSelectedBrand}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            selectedModification={selectedModification}
            setSelectedModification={setSelectedModification}
            selectedRoofType={selectedRoofType}
            setSelectedRoofType={setSelectedRoofType}
            showResults={showResults}
            setShowResults={setShowResults}
            handleSearch={handleSearch}
            racks={racks}
            filteredRacks={filteredRacks}
            setFilteredRacks={setFilteredRacks}
            availableRacks={availableRacks}
            setAvailableRacks={setAvailableRacks}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            filters={filters}
            setFilters={setFilters}
            resetFilters={resetFilters}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            dropdownRef={dropdownRef}
            currentSlide={currentSlide}
            handleNextSlide={handleNextSlide}
            handlePrevSlide={handlePrevSlide}
            addToCart={addToCart}
            isInCart={isInCart}
            toggleFavorite={(rack) => toggleFavorite(rack, favorites, setFavorites)}
            isFavorite={(rackId) => isFavorite(rackId, favorites)}
          />
        );
    }
  };

  return (
    <div className={`max-w-md mx-auto min-h-screen pb-16 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50'
      }`}>
      <Header
        activeTab={activeTab}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        cart={cart}
        setActiveTab={setActiveTab}
        checkoutStep={checkoutStep}
      />

      <div className="p-4">
        {renderContent()}
      </div>

      {/* Bottom Navigation */}
      {(activeTab !== 'cart' || checkoutStep === 0) && (
        <BottomNavigation
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          darkMode={darkMode}
          cart={cart}
          setSelectedBrand={setSelectedBrand}
          setSelectedModel={setSelectedModel}
          setSelectedModification={setSelectedModification}
          setSelectedRoofType={setSelectedRoofType}
          setShowResults={setShowResults}
        />
      )}
    </div>
  );
}

export default App;